import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { LastBooking, lastBookingsCreated } from 'src/api/bookingsView';
import { PlaceType } from 'src/types';
import { defaultServerError } from 'src/utils/consts';
import { isBookingAvailable } from 'src/utils/isBookingAvailable';

import { messagesActions } from '../messages/slice';
import { StoreState, ThunkConfig } from '../types';
import { getUserPermissions } from '../user/selectors';

export interface LastBookings {
  [PlaceType.Desk]: LastBooking | undefined;
  [PlaceType.Room]: LastBooking | undefined;
  [PlaceType.Parking]: LastBooking | undefined;
}

export const fetchLastBookings = createAsyncThunk<
  LastBookings,
  string,
  ThunkConfig<string>
>(
  'lastBookings/fetchLastBookings',
  async (employeeNumber, { getState, dispatch, rejectWithValue }) => {
    try {
      const response = await lastBookingsCreated(employeeNumber);

      const bookings: Map<PlaceType, LastBooking> = new Map();

      for (const booking of response) {
        bookings.set(booking.placeType, booking);
      }

      const firstBooking = [...bookings.values()][0] as LastBooking | undefined;
      const lastDeskBooking = bookings.get(PlaceType.Desk) ?? firstBooking;
      const lastRoomBooking = bookings.get(PlaceType.Room) ?? firstBooking;
      const lastParkingBooking = bookings.get(PlaceType.Parking);

      const permissions = getUserPermissions(
        getState() as StoreState,
      )?.permissions;

      return {
        [PlaceType.Desk]: isBookingAvailable({
          ...lastDeskBooking,
          placeType: PlaceType.Desk,
          permissions,
        })
          ? lastDeskBooking
          : undefined,
        [PlaceType.Room]: isBookingAvailable({
          ...lastRoomBooking,
          placeType: PlaceType.Room,
          permissions,
        })
          ? lastRoomBooking
          : undefined,
        [PlaceType.Parking]: isBookingAvailable({
          ...lastParkingBooking,
          placeType: PlaceType.Parking,
          permissions,
        })
          ? lastParkingBooking
          : undefined,
      };
    } catch (e) {
      dispatch(messagesActions.show({ message: defaultServerError }));
      return rejectWithValue((e as AxiosError).message);
    }
  },
);
